// -----------------------------------------------------------
// FloraIQ - StageDetailDrawer
// -----------------------------------------------------------

import type { StageInfo, ChecklistItem, PendingIssue } from "@/mocks/operational";
import StageStatusBadge from "./StageStatusBadge";

interface StageDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  stage: StageInfo;
  stageLabel: string;
  checklist: ChecklistItem[];
  pendencias: PendingIssue[];
}

export default function StageDetailDrawer({ isOpen, onClose, stage, stageLabel, checklist, pendencias }: StageDetailDrawerProps) {
  if (!isOpen) return null;

  const completedCount = checklist.filter((c) => c.concluido).length;
  const totalCount = checklist.length;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-md h-full shadow-lg flex flex-col slide-in-from-right overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200/50 shrink-0">
          <div>
            <p className="text-xs text-foreground-400 font-medium">Etapa {stage.id}</p>
            <h3 className="text-base font-bold text-foreground-900 font-heading">{stageLabel}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Status card */}
          <div className="rounded-card border border-background-200/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <StageStatusBadge status={stage.status} />
              <span className="text-xs text-foreground-500">{stage.progresso}% concluído</span>
            </div>
            <div className="w-full bg-background-100 rounded-full h-2">
              <div className="bg-primary-500 h-2 rounded-full transition-all duration-700" style={{ width: `${stage.progresso}%` }}></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-background-100">
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Responsável</p>
                <p className="text-sm text-foreground-800 font-medium">{stage.responsavel}</p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Data Prevista</p>
                <p className="text-sm text-foreground-800 font-medium">{stage.dataPrevista}</p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Data Conclusão</p>
                <p className="text-sm text-foreground-800 font-medium">{stage.dataConcluida || "-"}</p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Documentos</p>
                <p className="text-sm text-foreground-800 font-medium">{stage.documentosVinculados} vinculados</p>
              </div>
            </div>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-foreground-900">Checklist de Atividades</h4>
              <span className="text-xs text-foreground-500">{completedCount}/{totalCount}</span>
            </div>
            <div className="space-y-1.5">
              {checklist.map((item) => (
                <div key={item.id} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-background-50/70 border border-background-100/70">
                  <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 ${item.concluido ? "bg-green-100 text-green-600" : "bg-background-100 text-foreground-400"}`}>
                    <i className={`${item.concluido ? "ri-check-line" : "ri-subtract-line"} text-xs`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${item.concluido ? "text-foreground-600 line-through" : "text-foreground-800"}`}>{item.descricao}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-foreground-400">{item.responsavel}</span>
                      <span className="text-[10px] text-foreground-300">•</span>
                      <span className="text-[10px] text-foreground-400">{item.prazo}</span>
                    </div>
                    {item.observacao && (
                      <p className="text-[10px] text-foreground-400 mt-0.5 italic">{item.observacao}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pendências */}
          {pendencias.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-foreground-900">Pendências</h4>
                <span className="text-xs text-orange-600 font-medium">{pendencias.length} pendentes</span>
              </div>
              <div className="space-y-1.5">
                {pendencias.map((p) => (
                  <div key={p.id} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-orange-100 bg-orange-50/50">
                    <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${p.prioridade === "alta" ? "bg-red-500" : p.prioridade === "media" ? "bg-orange-500" : "bg-yellow-500"}`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground-800">{p.item}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-foreground-400">{p.responsavel}</span>
                        <span className="text-[10px] text-foreground-300">•</span>
                        <span className="text-[10px] text-foreground-400">Prazo: {p.prazo}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      p.status === "resolvida" ? "bg-green-100 text-green-700" :
                      p.status === "em_andamento" ? "bg-blue-100 text-blue-700" :
                      "bg-orange-100 text-orange-700"
                    }`}>
                      {p.status === "resolvida" ? "Resolvida" : p.status === "em_andamento" ? "Em andamento" : "Pendente"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blockages */}
          {stage.status === "bloqueada" && (
            <div className="p-4 rounded-card border border-red-200 bg-red-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-forbid-line text-red-600 text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-red-700">Etapa Bloqueada</h4>
              </div>
              <p className="text-xs text-red-600">Esta etapa possui bloqueios que precisam ser resolvidos antes de prosseguir.</p>
            </div>
          )}

          {/* Histórico */}
          <div>
            <h4 className="text-sm font-bold text-foreground-900 mb-2">Histórico de Alterações</h4>
            <div className="space-y-2">
              {[
                { data: "28/06/2026", descricao: "Progresso atualizado para " + stage.progresso + "%", usuario: "Sistema" },
                { data: "15/06/2026", descricao: "Etapa iniciada", usuario: stage.responsavel },
                { data: "01/06/2026", descricao: "Etapa planejada", usuario: "Coordenador Técnico" },
              ].map((h, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-background-300 mt-1.5 shrink-0"></div>
                  <div>
                    <p className="text-xs text-foreground-700">{h.descricao}</p>
                    <p className="text-[10px] text-foreground-400">{h.data} • {h.usuario}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-background-200/50 flex items-center gap-2 shrink-0 flex-wrap">
          {stage.status === "em_andamento" && (
            <button className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap flex-1">
              <i className="ri-check-line mr-1"></i>Marcar como concluída
            </button>
          )}
          {stage.status === "concluida" && (
            <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap flex-1">
              <i className="ri-refresh-line mr-1"></i>Reabrir etapa
            </button>
          )}
          <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
            <i className="ri-add-line mr-1"></i>Pendência
          </button>
          <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
            <i className="ri-file-chart-line mr-1"></i>Relatório
          </button>
        </div>
      </div>
    </div>
  );
}